import { DashboardEgpComponent } from '../dashboard/egp/dashboard-egp.component';
import { DashboardEppComponent } from '../dashboard/epp/dashboard-epp.component';
import { DashboardSmwComponent } from '../dashboard/smw/dashboard-smw.component';
import { DashboardWarningComponent } from '../dashboard/warning/dashboard-warning.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../shared/components/confirm-dialog/confirm-dialog.component';
import { LoadingOverlayComponent } from '../shared/components/loading-overlay/loading-overlay.component';

import { AuthService } from '../auth/auth.service';
import { EgpSeason, EgpService } from '../egp/egp.service';
import { LanguageService } from '../shared/services/language.service';
import { ThemeService } from '../shared/services/theme.service';
import { OperationalReportService } from './operational-report.service';

import { DatexPipe } from '../shared/datex.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import {
  categoryColorSet,
  categoryLabel,
  PREDEFINED_CATEGORIES,
} from './operational-report.categories';
import {
  CreateOpReportBody,
  OpReport,
  OpReportContent,
  UpdateOpReportBody,
} from './operational-report.model';
import moment, { Moment } from 'moment/moment';
import { firstValueFrom } from 'rxjs';

export const MODULES = [
  CommonModule,
  MatFormFieldModule,
  MatDatepickerModule,
  ReactiveFormsModule,
  MatButtonModule,
  MatCardModule,
  MatMenuModule,
  MatTooltipModule,
  MatDividerModule,
  MatDialogModule,
];
export const COMPONENTS = [
  FaIconComponent,
  DashboardEgpComponent,
  DashboardWarningComponent,
  DashboardSmwComponent,
  DashboardEppComponent,
  LoadingOverlayComponent,
];
export const PIPES = [TranslatePipe, DatexPipe];

@Component({
  selector: 'app-op-report-edit',
  templateUrl: './operational-report.edit.component.html',
  styleUrls: ['./operational-report.edit.component.scss'],
  providers: [OperationalReportService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class OperationalReportEditComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private egpService = inject(EgpService);
  private operationalReportService = inject(OperationalReportService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translate = inject(TranslateService);
  public languageService = inject(LanguageService);
  private elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private themeService = inject(ThemeService);
  private dialog = inject(MatDialog);

  private customerId = 0;

  // --- PDF-Export ----------------------------------------------------------
  // Ersetzt den Browser-nativen Druck (Strg+P) als primären Weg: weder
  // Chrome noch Safari paginieren die Einsatzpunktprognose-Tabelle beim
  // nativen Drucken zuverlässig über mehrere Seiten (siehe SCSS-Kommentare
  // bei .pdf-export-mode für die ausführliche, erfolglose Fehlersuche).
  isExportingPdf = signal(false);

  // --- Modus (Neu / Bearbeiten / Ansehen) --------------------------------
  // Wird über den Query-Param ?mode=edit|view (+ ?id=...) gesteuert
  // (Default: 'new', keine id).
  requestedMode = signal<'new' | 'edit' | 'view'>('new');

  // id des geladenen/gespeicherten op_reports-Eintrags. Null solange der
  // aktuelle Einsatz noch nicht gespeichert wurde ("Neu").
  opReportId = signal<number | null>(null);
  isLoadingReport = signal(false);

  // Bearbeiten ist nur möglich, solange der Einsatz nicht abgeschlossen ist –
  // ist der Status bereits 'closed', wird ein angeforderter 'edit'-Modus
  // automatisch auf 'view' heruntergestuft.
  mode = computed<'new' | 'edit' | 'view'>(() => {
    const requested = this.requestedMode();
    if (requested === 'edit' && this.operationStatus() === 'closed') {
      return 'view';
    }
    return requested;
  });

  isReadonly = computed(() => this.mode() === 'view');

  // Die Wetter-Snapshots (EGP/Warnungen/Messwerte/EPP) werden nur beim ERSTEN
  // Speichern eines neuen Einsatzes festgeschrieben (siehe persistReport())
  // – ein nachträglicher Wechsel der Station/Region/des Parametersets über
  // die Zahnrad-Menüs würde daher nicht mehr zum gespeicherten Snapshot
  // passen. Die Menüs dürfen deshalb nur vor dem ersten Speichern (noch
  // keine opReportId) bedient werden, unabhängig vom sonstigen
  // Bearbeiten/Ansehen-Modus.
  canEditWeatherConfig = computed(
    () => !this.isReadonly() && this.opReportId() === null
  );

  // --- "Wetter jetzt"-Bereich (rechte Karte) -----------------------------
  // Zeigt die vier Elemente aus dem Mockup (EGP, Warnungen, Messwerte,
  // Einsatzpunktprognose). Auswahllogik jeweils analog zu den init*()-
  // Methoden im Dashboard, hier aber auf die erste aktive Option reduziert.
  // Jede Karte sammelt zusätzlich ALLE aktiven Optionen (*Options) fürs
  // Zahnrad-Menü, mit dem der User wie im Dashboard die angezeigte Region/
  // Station wechseln kann.
  //
  // Beim ersten Speichern eines "Neu"-Einsatzes werden die dann aktuell
  // geladenen Live-Rohdaten (egpLiveResponse/egwLiveDisplayed/
  // eppLiveResponse/smwLiveResponse, über die *ResponseChange/-Change
  // Outputs der Dashboard-Komponenten befüllt) als Snapshot mitgespeichert.
  // Beim erneuten Öffnen (mode=edit/view + id) werden stattdessen die
  // gespeicherten Snapshots (egpSnapshot/egwSnapshot/eppSnapshot/
  // smwSnapshot) an die Dashboard-Komponenten durchgereicht statt live
  // nachzuladen.

  egpConfig = signal<{
    region: string;
    name: string;
    season: EgpSeason;
    view: 'riskbox' | 'text';
  } | null>(null);
  egpOptions = signal<
    Array<{ region: string; name: string; season: EgpSeason }>
  >([]);
  egpRefresh = signal(0);
  egpUpdatedAt = signal<string | null>(null);
  egpSnapshot = signal<any>(null);
  // Signal statt einfachem Feld, da hasEgpText()/currentEgpText() darauf
  // als computed() reagieren (für den "Kopiere Text aus EGP"-Textbaustein).
  private egpLiveResponse = signal<any>(null);

  // Warnungen (EGW)
  egwConfig = signal<{
    region: string;
    name: string;
    season: 'summer' | 'winter';
  } | null>(null);
  egwOptions = signal<
    Array<{ region: string; name: string; season: 'summer' | 'winter' }>
  >([]);
  warnRefresh = signal(0);
  egwSnapshot = signal<any>(null);
  private egwLiveDisplayed: any = null;

  // Messwerte (SMW) – ersetzt die vorherige Radar-Kachel (siehe
  // OpReport.smw). Analog zu EGP/EGW/EPP wird hier die zum
  // Speicherzeitpunkt abgerufene Rohantwort als Snapshot mitgespeichert
  // (nicht wie bei Radar nur ein Zeitstempel), da die Kachel echte
  // Stationsmesswerte zeigt statt einer deterministisch aus Zeitstempel +
  // Region gebauten Kachel-URL.
  smwConfig = signal<{
    stationType: 'gma' | 'synop';
    stations: string[];
    stationNames: Record<string, string>;
    card: string;
  } | null>(null);
  smwOptions = signal<
    Array<{
      stationType: 'gma' | 'synop';
      stations: string[];
      stationNames: Record<string, string>;
      card: string;
    }>
  >([]);
  smwRefresh = signal(0);
  smwSnapshot = signal<any>(null);
  private smwLiveResponse: any = null;

  // Einsatzpunktprognose (EPP) – das Zahnrad-Menü erlaubt sowohl den
  // Wechsel der Station (eppOptions) als auch des Parametersets
  // (eppConfig().allParametersets); die Auflösung bleibt auf der ersten
  // aktiven Kombination fixiert.
  eppConfig = signal<{
    station: any;
    resolution: any;
    parameterset: string;
    allParametersets: string[];
    view: 'table';
  } | null>(null);
  eppOptions = signal<Array<any>>([]);
  private eppProductContext: {
    resolution: any;
    allParametersets: string[];
    parameterset: string;
  } | null = null;
  eppLastIssueTime = signal<string | null>(null);
  eppRefresh = signal(0);
  eppSnapshot = signal<any>(null);
  private eppLiveResponse: any = null;

  async ngOnInit(): Promise<void> {
    this.customerId = this.auth.getCustomerDocument()['customerId'];

    const queryMode = this.route.snapshot.queryParamMap.get('mode');
    if (queryMode === 'edit' || queryMode === 'view') {
      this.requestedMode.set(queryMode);
    }

    const idParam = this.route.snapshot.queryParamMap.get('id');
    const id = idParam ? Number(idParam) : null;
    if (id) {
      this.opReportId.set(id);
      // Erst den gespeicherten Report abwarten, damit die Snapshots
      // (egpConfig()/egwConfig()/eppConfig()) schon gesetzt sind, bevor
      // die init*()-Methoden unten prüfen, ob sie noch live nachladen
      // müssen – sonst könnte kurz live geladen werden, bevor der
      // (künstlich verzögerte) Snapshot eintrifft.
      await this.loadReport(id);
    } else {
      // "Neu": Einsatzleiter-Namen mit dem eigenen Profil vorbelegen, vom
      // User im Formular aber änderbar (z.B. wenn ein Kollege den Einsatz
      // tatsächlich leitet).
      this.leaderNameControl.setValue(this.currentUserDisplayName());
      // Anders als der Einsatzleiter-Name NICHT änderbar – wer den Einsatz
      // eröffnet, steht für die Timeline fest (siehe operationOpenedByName).
      this.operationOpenedByName.set(this.currentUserDisplayName());
      // Erst NACH dem Vorbelegen mit dem eigenen Profil – der vorbelegte
      // Name allein soll noch nicht als "Änderung" zählen, siehe
      // hasUnsavedChanges().
      this.captureOriginalSnapshot();
    }

    // Optionen (verfügbare Regionen/Stationen) werden immer live ermittelt.
    // Ist bereits ein Snapshot geladen, wird dort NICHT zusätzlich live
    // nachgeladen – siehe Guards in den jeweiligen select*() Aufrufen unten.
    this.initEgp();
    this.initWarnings();
    this.initEinsatzpunktprognose();
    this.initSmw();

    window.addEventListener('beforeprint', this.prepareForPrint);
    window.addEventListener('afterprint', this.restoreAfterPrint);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeprint', this.prepareForPrint);
    window.removeEventListener('afterprint', this.restoreAfterPrint);
  }

  // Bekannter Chrome-Druckfehler: Container mit overflow:auto/scroll, die
  // gescrollt wurden (z.B. die Einsatzpunktprognose-Tabelle oder die
  // EGP-/Warnungen-Textbereiche), zeigen im Ausdruck nur den zum Zeitpunkt
  // des Druckens sichtbaren Scroll-Ausschnitt – selbst wenn @media print
  // (siehe operational-report.edit.component.scss) overflow:visible und
  // height:auto setzt. scrollTop muss dafür zusätzlich per JS auf 0
  // zurückgesetzt werden, bevor der Druckdialog öffnet.
  private prepareForPrint = (): void => {
    document
      .querySelectorAll(
        '.dashboard-table-container, .dashboard-egp-text-zone, .vorab-scroll-container'
      )
      .forEach(element => {
        (element as HTMLElement).scrollTop = 0;
      });

    // Die Einsatzpunktprognose-Tabelle ist je nach Parameterset/Station
    // unterschiedlich breit (Anzahl Spalten variiert) und dadurch oft
    // breiter als die Druckseite. Zwei frühere Lösungsversuche sind daran
    // gescheitert, dass Chrome die automatische Seiten-Paginierung für
    // <table>-Elemente, die breiter als die Seite sind, komplett abbricht:
    //   1. table-layout:fixed ließ ungleich breite Spalten fast auf 0
    //      kollabieren.
    //   2. transform:scale (rein optische Verkleinerung, ändert die
    //      Layout-Breite nicht) hätte overflow-x:hidden auf einem
    //      Vorfahren gebraucht, um die Seite horizontal nicht zu sprengen –
    //      das zwingt laut CSS-Spec aber die overflow-y-Achse desselben
    //      Elements von "visible" auf "auto" (sobald eine Achse nicht
    //      "visible" ist, wird die andere automatisch zu "auto"), und
    //      "auto" macht das Element für den Druck NICHT mehr umbrechbar
    //      ("monolithisch") – die Tabelle klappte dadurch wieder auf eine
    //      einzige Seite zusammen.
    // Die Schriftgröße ändert dagegen die TATSÄCHLICHE Layout-Breite (statt
    // nur die Optik) – dadurch entsteht gar kein horizontaler Überlauf, und
    // overflow-x:hidden ist an keiner Stelle nötig, sodass overflow-y für
    // alle Vorfahren "visible" bleibt und die normale mehrseitige
    // Fragmentierung erhalten bleibt.
    const table = document.querySelector<HTMLElement>(
      '.weather-now__epp table[mat-table]'
    );
    if (table) {
      table.style.fontSize = '';
      const container = table.closest<HTMLElement>(
        '.dashboard-table-container'
      );
      const availableWidth = container?.clientWidth || table.clientWidth;
      const naturalWidth = table.scrollWidth;
      if (naturalWidth > availableWidth && availableWidth > 0) {
        const currentFontSize = parseFloat(
          getComputedStyle(table).fontSize || '15'
        );
        const scale = availableWidth / naturalWidth;
        table.style.fontSize = `${currentFontSize * scale}px`;
      }
    }
    // Sticky Header/erste Spalte der EPP-Tabelle werden nicht mehr von hier
    // aus per DOM-Manipulation bekämpft (verlor zuverlässig den Wettlauf
    // gegen Angular CDKs eigene Sticky-Neuberechnung), sondern direkt in
    // EppTableComponent für den Druck deaktiviert – siehe isPrinting() in
    // epp-table.component.ts/.html.
  };

  private restoreAfterPrint = (): void => {
    const table = document.querySelector<HTMLElement>(
      '.weather-now__epp table[mat-table]'
    );
    if (table) {
      table.style.fontSize = '';
    }
  };

  private loadReport(id: number): Promise<void> {
    this.isLoadingReport.set(true);

    this.operationalReportService.getComments(id).subscribe(comments => {
      this.operationComments.set(
        comments.map(comment => ({
          text: comment.content.text,
          createdAt: moment(comment.content.createdAt),
          commentId: comment.id,
          authorName: comment.content.authorName,
        }))
      );
    });

    return firstValueFrom(
      this.operationalReportService.getReport(this.customerId, id)
    ).then(report => {
      this.isLoadingReport.set(false);
      if (report) {
        this.applyReport(report);
      }
    });
  }

  // Name des Einsatzleiters kommt aus dem eigenen user_profile im
  // localStorage – eine Namensauflösung für andere User ist ohne
  // entsprechende Berechtigung nicht garantiert möglich, daher wird hier
  // (wie vom User bestätigt) bewusst nur das eigene Profil gelesen:
  // metadata.name, sonst email als Fallback.
  private currentUserDisplayName(): string {
    const profile = this.auth.getUserProfile();
    return profile?.['metadata']?.['name'] || profile?.['email'] || '';
  }

  private applyReport(report: OpReport): void {
    this.operationStatus.set(report.content.status);
    this.operationOpenedAt.set(moment(report.start));
    this.operationLastUpdatedAt.set(moment(report.end ?? report.start));
    this.operationClosedAt.set(report.end ? moment(report.end) : null);
    this.operationOpenedByName.set(report.content.openedByName ?? '');
    this.operationClosedByName.set(report.content.closedByName ?? null);
    this.operationCategories.set(report.content.categories ?? []);
    this.situationReportControl.setValue(report.content.situationReport ?? '');
    this.leaderNameControl.setValue(report.content.leaderName ?? '');
    this.captureOriginalSnapshot();

    if (report.egp) {
      this.egpConfig.set(report.egp.config);
      this.egpSnapshot.set(report.egp.data);
      this.egpUpdatedAt.set(report.egp.data?.textObject?.prescribeAt ?? null);
    }
    if (report.egw) {
      this.egwConfig.set(report.egw.config);
      this.egwSnapshot.set(report.egw.data);
    }
    if (report.epp) {
      this.eppConfig.set(report.epp.config);
      this.eppSnapshot.set(report.epp.data);
      this.eppLastIssueTime.set(report.epp.data?.last_issue_time ?? null);
    }
    if (report.smw) {
      this.smwConfig.set(report.smw.config);
      this.smwSnapshot.set(report.smw.data);
    }
  }

  async initEgp(): Promise<void> {
    if (this.auth.isPigeonCustomer()) {
      return;
    }

    const customerDocument = this.auth.getCustomerDocument();
    const egpProducts = (customerDocument['egpProducts'] ?? []).concat(
      customerDocument['egpSummerProducts'] ?? []
    );

    const options: Array<{ region: string; name: string; season: EgpSeason }> =
      [];
    for (const egpProduct of egpProducts) {
      if (
        egpProduct !== undefined &&
        (this.auth.isProductActive('egpProducts', egpProduct.id) ||
          this.auth.isProductActive('egpSummerProducts', egpProduct.id))
      ) {
        const region = egpProduct['areaId'];
        const season =
          egpProduct['classType'] === 'EGPProduct'
            ? EgpSeason.WINTER
            : EgpSeason.SUMMER;
        if (options.some(o => o.region === region && o.season === season)) {
          continue;
        }
        const regions = await this.egpService.getAllEgpAreas(season);
        const name = regions.find(a => a.id == region)?.name;
        options.push({ region, name, season });
      }
    }

    this.egpOptions.set(options);
    // Ist schon eine Konfiguration gesetzt (Snapshot aus einem geladenen
    // Report), nicht zusätzlich die erste Option live auswählen/abrufen.
    if (options.length > 0 && !this.egpConfig()) {
      const preference = this.loadPreference<{
        region: string;
        season: EgpSeason;
      }>(this.EGP_PREFERENCE_KEY);
      const defaultOption =
        options.find(
          o =>
            o.region === preference?.region && o.season === preference?.season
        ) ?? options[0];
      // persistPreference=false: automatische Vorbelegung, keine bewusste
      // User-Auswahl – siehe selectEppStation() für die Begründung.
      this.selectEgp(defaultOption, false);
    }
  }

  selectEgp(
    option: { region: string; name: string; season: EgpSeason },
    persistPreference = true
  ): void {
    this.egpUpdatedAt.set(null);
    this.egpSnapshot.set(null); // manuelle Auswahl lädt immer live nach
    this.egpConfig.set({ ...option, view: 'text' });
    if (persistPreference) {
      this.savePreference(this.EGP_PREFERENCE_KEY, {
        region: option.region,
        season: option.season,
      });
    }
  }

  onEgpDtgChange(dtg: string | null): void {
    this.egpUpdatedAt.set(dtg);
  }

  onEgpResponseChange(response: any): void {
    this.egpLiveResponse.set(response);
  }

  async initWarnings(): Promise<void> {
    const customerDocument = this.auth.getCustomerDocument();
    const egwProducts = (customerDocument['egwProducts'] ?? []).concat(
      customerDocument['egwSummerProducts'] ?? []
    );

    const options: Array<{
      region: string;
      name: string;
      season: 'summer' | 'winter';
    }> = [];
    for (const egwProduct of egwProducts) {
      if (
        egwProduct !== undefined &&
        (this.auth.isProductActive('egwProducts', egwProduct.id) ||
          this.auth.isProductActive('egwSummerProducts', egwProduct.id))
      ) {
        const season: 'summer' | 'winter' =
          egwProduct['classType'] === 'EGWProduct' ? 'winter' : 'summer';
        const region =
          season === 'winter' ? egwProduct['areaId'] : egwProduct['id'];
        if (options.some(o => o.region === region && o.season === season)) {
          continue;
        }
        const name =
          season === 'winter'
            ? (await this.egpService.getAllEgpAreas(EgpSeason.WINTER)).find(
                a => a.id == region
              )?.name
            : egwProduct['points']?.[0]?.searchName;
        options.push({ region, name, season });
      }
    }

    this.egwOptions.set(options);
    if (options.length > 0 && !this.egwConfig()) {
      const preference = this.loadPreference<{
        region: string;
        season: 'summer' | 'winter';
      }>(this.EGW_PREFERENCE_KEY);
      const defaultOption =
        options.find(
          o =>
            o.region === preference?.region && o.season === preference?.season
        ) ?? options[0];
      // persistPreference=false: automatische Vorbelegung, keine bewusste
      // User-Auswahl – siehe selectEppStation() für die Begründung.
      this.selectWarning(defaultOption, false);
    }
  }

  selectWarning(
    option: {
      region: string;
      name: string;
      season: 'summer' | 'winter';
    },
    persistPreference = true
  ): void {
    this.egwSnapshot.set(null); // manuelle Auswahl lädt immer live nach
    this.egwConfig.set(option);
    if (persistPreference) {
      this.savePreference(this.EGW_PREFERENCE_KEY, {
        region: option.region,
        season: option.season,
      });
    }
  }

  onDisplayedWarningChange(warning: any): void {
    this.egwLiveDisplayed = warning;
  }

  initSmw(): void {
    const customerDocument = this.auth.getCustomerDocument();
    const smwProducts = customerDocument['smwProducts'] ?? [];

    const stations: Record<'gma' | 'synop', string[]> = { gma: [], synop: [] };
    const names: Record<string, string> = {};
    for (const smwProduct of smwProducts) {
      if (!this.auth.isProductActive('smwProducts', smwProduct.id)) {
        continue;
      }
      for (const station of smwProduct.stations ?? []) {
        if (
          !this.auth.isUserAllowedToAccessToProduct(
            smwProduct.id,
            station.stationId
          )
        ) {
          continue;
        }
        const stationType: 'gma' | 'synop' =
          station.stationType === 'gma' ? 'gma' : 'synop';
        if (!stations[stationType].includes(station.stationId)) {
          stations[stationType].push(station.stationId);
          names[station.stationId] = station.overwriteStationName;
        }
      }
    }

    const options: Array<{
      stationType: 'gma' | 'synop';
      stations: string[];
      stationNames: Record<string, string>;
      card: string;
    }> = [];
    for (const stationType of ['gma', 'synop'] as const) {
      if (stations[stationType].length > 0) {
        options.push({
          stationType,
          stations: stations[stationType],
          stationNames: names,
          card: 'smw_' + stationType,
        });
      }
    }

    this.smwOptions.set(options);
    // Ist schon eine Konfiguration gesetzt (Snapshot aus einem geladenen
    // Report), nicht zusätzlich die erste Option live auswählen.
    if (options.length > 0 && !this.smwConfig()) {
      const preference = this.loadPreference<{ stationType: 'gma' | 'synop' }>(
        this.SMW_PREFERENCE_KEY
      );
      const defaultOption =
        options.find(o => o.stationType === preference?.stationType) ??
        options[0];
      // persistPreference=false: automatische Vorbelegung, keine bewusste
      // User-Auswahl – siehe selectEppStation() für die Begründung.
      this.selectSmw(defaultOption, false);
    }
  }

  selectSmw(
    option: {
      stationType: 'gma' | 'synop';
      stations: string[];
      stationNames: Record<string, string>;
      card: string;
    },
    persistPreference = true
  ): void {
    this.smwSnapshot.set(null); // manuelle Auswahl lädt immer live nach
    this.smwConfig.set(option);
    if (persistPreference) {
      this.savePreference(this.SMW_PREFERENCE_KEY, {
        stationType: option.stationType,
      });
    }
  }

  onSmwResponseChange(response: any): void {
    this.smwLiveResponse = response;
  }

  // Zuletzt manuell über die Zahnrad-Menüs gewählte Region/Station/
  // Parameterset je Wetter-Kachel – gespeichert in
  // user_profile.metadata.global (gleiches Muster wie
  // DashboardEppComponent.saveEppSettings()), damit sie beim Anlegen eines
  // neuen Einsatzes als Vorbelegung dienen. Der geräteübergreifende Sync
  // läuft automatisch über auth.updateUserData().
  private loadPreference<T>(key: string): T | null {
    return this.auth.getUserProfile()?.['metadata']?.['global']?.[key] ?? null;
  }

  private savePreference(key: string, value: unknown): void {
    const userProfile = this.auth.getUserProfile();
    const global = { ...(userProfile?.['metadata']?.['global'] ?? {}) };
    global[key] = value;
    this.auth.updateUserData('global', global);
  }

  private readonly EGP_PREFERENCE_KEY = 'opReportEgp';
  private readonly EPP_PREFERENCE_KEY = 'opReportEpp';
  private readonly EGW_PREFERENCE_KEY = 'opReportEgw';
  private readonly SMW_PREFERENCE_KEY = 'opReportSmw';

  private loadEppPreference(): {
    stationId: any;
    parameterset: string;
  } | null {
    return this.loadPreference(this.EPP_PREFERENCE_KEY);
  }

  private saveEppPreference(stationId: any, parameterset: string): void {
    this.savePreference(this.EPP_PREFERENCE_KEY, { stationId, parameterset });
  }

  initEinsatzpunktprognose(): void {
    const customerDocument = this.auth.getCustomerDocument();
    const eppProducts = customerDocument['eppProducts'] ?? [];
    const preference = this.loadEppPreference();

    for (const eppProduct of eppProducts) {
      if (!this.auth.isProductActive('eppProducts', eppProduct.id)) {
        continue;
      }

      const stations = (eppProduct['stations'] ?? []).filter(station =>
        this.auth.isUserAllowedToAccessToProduct(
          eppProduct.id,
          station.stationId
        )
      );
      if (stations.length === 0) {
        continue;
      }

      const resolution = (eppProduct['resolutions'] ?? []).find(
        (r: any) => r.days >= 1
      );
      if (!resolution) {
        continue;
      }

      const parametersets = [...(eppProduct['parameter_sets'] ?? [])].sort(
        (a, b) => (a < b ? 1 : -1)
      );
      const defaultParameterset =
        preference?.parameterset &&
        parametersets.includes(preference.parameterset)
          ? preference.parameterset
          : parametersets[0];
      if (!defaultParameterset) {
        continue;
      }

      this.eppProductContext = {
        resolution,
        allParametersets: eppProduct['parameter_sets'],
        parameterset: defaultParameterset,
      };
      this.eppOptions.set(stations);
      // Ist schon eine Konfiguration gesetzt (Snapshot aus einem geladenen
      // Report), nicht zusätzlich die erste Station live auswählen.
      if (!this.eppConfig()) {
        const defaultStation =
          stations.find(s => s.stationId === preference?.stationId) ??
          stations[0];
        // persistPreference=false: dies ist die automatische Vorbelegung,
        // keine bewusste User-Auswahl – soll eine evtl. vorhandene
        // gespeicherte Präferenz nicht überschreiben (z.B. falls die
        // gespeicherte Station diesmal nicht verfügbar war und auf
        // stations[0] zurückgefallen wurde).
        this.selectEppStation(defaultStation, false);
      }
      return; // erstes aktives Produkt reicht fürs Erste
    }
  }

  selectEppStation(station: any, persistPreference = true): void {
    if (!this.eppProductContext) {
      return;
    }
    this.eppLastIssueTime.set(null);
    this.eppSnapshot.set(null); // manuelle Auswahl lädt immer live nach
    this.eppConfig.set({
      station,
      resolution: this.eppProductContext.resolution,
      parameterset: this.eppProductContext.parameterset,
      allParametersets: this.eppProductContext.allParametersets,
      view: 'table',
    });
    if (persistPreference) {
      this.saveEppPreference(
        station.stationId,
        this.eppProductContext.parameterset
      );
    }
  }

  selectEppParameterset(parameterset: string, persistPreference = true): void {
    const current = this.eppConfig();
    if (!current || !this.eppProductContext) {
      return;
    }
    this.eppLastIssueTime.set(null);
    this.eppSnapshot.set(null); // manuelle Auswahl lädt immer live nach
    this.eppProductContext = { ...this.eppProductContext, parameterset };
    this.eppConfig.set({ ...current, parameterset });
    if (persistPreference) {
      this.saveEppPreference(current.station.stationId, parameterset);
    }
  }

  onEppLastIssueTimeChange(lastIssueTime: string): void {
    this.eppLastIssueTime.set(lastIssueTime);
  }

  onEppResponseChange(response: any): void {
    this.eppLiveResponse = response;
  }

  // --- "Einsatz"-Formular (linke Karte) ---------------------------------

  readonly predefinedCategories = PREDEFINED_CATEGORIES;

  operationStatus = signal<'open' | 'closed'>('open');
  operationOpenedAt = signal<Moment>(moment());
  operationLastUpdatedAt = signal<Moment>(moment());
  // Nur gesetzt, wenn der Einsatz tatsächlich abgeschlossen wurde
  // (report.end) – für die Anzeige des Geschlossen-Zeitpunkts in der
  // Detailansicht.
  operationClosedAt = signal<Moment | null>(null);

  // Für die Bearbeitungs-Timeline ("wer hat wann was gemacht") – siehe
  // OpReportContent.openedByName/closedByName. Bei "Neu" wird
  // operationOpenedByName direkt mit dem eigenen Profil vorbelegt (siehe
  // ngOnInit), operationClosedByName erst beim tatsächlichen Abschließen
  // (siehe onCompleteOperation()) gesetzt.
  operationOpenedByName = signal<string>('');
  operationClosedByName = signal<string | null>(null);

  // Bei "Neu" mit dem eigenen Profilnamen vorbelegt (siehe ngOnInit()),
  // aber änderbar – z.B. falls ein Kollege den Einsatz tatsächlich leitet.
  leaderNameControl = new FormControl<string>('');

  // "Neu" startet ohne vorausgewählte Kategorien – die werden erst über
  // den +-Button (vordefiniert oder Freitext) hinzugefügt.
  operationCategories = signal<Array<{ key: string }>>([]);

  // Sommer, wenn eine Sommer-EGP aktiv ist, oder – falls keine EGP aktiv
  // ist – wenn das heutige Datum zwischen 15.4. und 1.11. liegt (sonst
  // Winter). Ist eine Winter-EGP aktiv, ist es entsprechend Winter.
  private isSummerForCategories(): boolean {
    const egpSeason = this.egpConfig()?.season;
    if (egpSeason) {
      return egpSeason === EgpSeason.SUMMER;
    }
    const today = moment();
    const summerStart = moment({ year: today.year(), month: 3, day: 15 }); // 15. April
    const summerEnd = moment({ year: today.year(), month: 10, day: 1 }); // 1. November
    return today.isSameOrAfter(summerStart) && today.isSameOrBefore(summerEnd);
  }

  // Noch nicht ausgewählte vordefinierte Kategorien, sortiert: saison-
  // unabhängige Kategorien (season: 'all', z.B. Kontrollfahrt) immer ganz
  // oben, danach aktuelle Saison zuerst (Sommer-Kategorien im Sommer, sonst
  // Winter).
  availablePredefinedCategories = computed(() => {
    const usedKeys = new Set(
      this.operationCategories().map(category => category.key)
    );
    const available = this.predefinedCategories.filter(
      predefined => !usedKeys.has(predefined.key)
    );
    const primarySeason = this.isSummerForCategories() ? 'summer' : 'winter';
    const rank = (season: 'summer' | 'winter' | 'all'): number => {
      if (season === 'all') {
        return -1;
      }
      return season === primarySeason ? 0 : 1;
    };
    return [...available].sort((a, b) => rank(a.season) - rank(b.season));
  });

  showCustomCategoryInput = signal(false);
  customCategoryControl = new FormControl<string>('');

  // Label-/Farbauflösung ausgelagert nach operational-report.categories.ts,
  // damit Edit- und Listen-Ansicht dieselbe Logik nutzen.
  readonly categoryLabel = categoryLabel;
  readonly categoryColorSet = categoryColorSet;

  situationReportControl = new FormControl<string>('');

  // commentId gesetzt = bereits über op-comments gespeichert. Fehlt er, ist
  // der Kommentar noch nicht persistiert (nur möglich bei "Neu", solange
  // der Einsatz selbst noch keine id hat) – wird beim Speichern des
  // Einsatzes automatisch mit der dann vergebenen id nachgereicht.
  operationComments = signal<
    Array<{
      text: string;
      createdAt: Moment;
      commentId?: number;
      authorName?: string;
    }>
  >([]);
  newCommentControl = new FormControl<string>('');

  // Bearbeitungs-Timeline ("wer hat wann was gemacht"): eröffnet + jeder
  // Kommentar + ggf. abgeschlossen, chronologisch sortiert – ersetzt die
  // vorherige reine Kommentarliste im "Kommentare und Aktivität"-Bereich.
  // Da immer mindestens der "eröffnet"-Eintrag vorhanden ist, ist diese
  // Liste (anders als vorher operationComments) nie leer.
  timelineEntries = computed<
    Array<{
      type: 'opened' | 'closed' | 'comment';
      at: Moment;
      authorName: string;
      text?: string;
    }>
  >(() => {
    const entries: Array<{
      type: 'opened' | 'closed' | 'comment';
      at: Moment;
      authorName: string;
      text?: string;
    }> = [
      {
        type: 'opened',
        at: this.operationOpenedAt(),
        authorName: this.operationOpenedByName(),
      },
      ...this.operationComments().map(comment => ({
        type: 'comment' as const,
        at: comment.createdAt,
        authorName: comment.authorName ?? '',
        text: comment.text,
      })),
    ];
    const closedAt = this.operationClosedAt();
    if (this.operationStatus() === 'closed' && closedAt) {
      entries.push({
        type: 'closed',
        at: closedAt,
        authorName: this.operationClosedByName() ?? '',
      });
    }
    return entries.sort((a, b) => a.at.valueOf() - b.at.valueOf());
  });

  addPredefinedCategory(predefined: { key: string }): void {
    this.operationCategories.update(categories => [
      ...categories,
      { key: predefined.key },
    ]);
  }

  removeCategory(index: number): void {
    this.operationCategories.update(categories =>
      categories.filter((_, i) => i !== index)
    );
  }

  openCustomCategoryInput(): void {
    this.showCustomCategoryInput.set(true);
  }

  confirmCustomCategory(): void {
    const text = this.customCategoryControl.value?.trim();
    if (!text) {
      return;
    }
    this.operationCategories.update(categories => [
      ...categories,
      { key: text },
    ]);
    this.cancelCustomCategory();
  }

  cancelCustomCategory(): void {
    this.customCategoryControl.setValue('');
    this.showCustomCategoryInput.set(false);
  }

  // Textbausteine: "Kein Einsatz notwendig" trägt einen festen Text ein,
  // "Kopiere Text aus Einsatzgebietsprognose" übernimmt den Lagebericht-
  // Text der gerade angezeigten EGP (live geladen oder Snapshot).
  hasEgpText = computed(() => !!this.currentEgpText());

  private currentEgpText(): string | null {
    const egpData = this.egpSnapshot() ?? this.egpLiveResponse();
    return egpData?.textObject?.texts?.[0]?.text ?? null;
  }

  insertNoOperationNeededText(): void {
    this.situationReportControl.setValue(
      this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.TEXT_BLOCK_NO_OPERATION_TEXT'
      )
    );
  }

  insertEgpText(): void {
    const text = this.currentEgpText();
    if (text) {
      this.situationReportControl.setValue(text);
    }
  }

  // Kommentare hängen eigentlich an einer bereits gespeicherten
  // op_reports-Zeile (op_comments.op_report_id). Damit der User trotzdem
  // schon bei "Neu" kommentieren kann, wird ohne vorhandene id nur lokal
  // vorgemerkt (kein commentId) – flushPendingComments() reicht das beim
  // Speichern des Einsatzes mit der dann vergebenen id nach.
  addComment(): void {
    const text = this.newCommentControl.value?.trim();
    if (!text) {
      return;
    }
    const createdAt = moment();
    const authorName = this.currentUserDisplayName();
    const id = this.opReportId();

    if (!id) {
      this.operationComments.update(comments => [
        ...comments,
        { text, createdAt, authorName },
      ]);
      this.newCommentControl.setValue('');
      return;
    }

    this.operationalReportService
      .createComment(id, {
        content: { text, createdAt: createdAt.toISOString(), authorName },
      })
      .subscribe(comment => {
        this.operationComments.update(comments => [
          ...comments,
          { text, createdAt, commentId: comment.id, authorName },
        ]);
        this.newCommentControl.setValue('');
      });
  }

  private flushPendingComments(id: number): Promise<void> {
    const pending = this.operationComments().filter(
      comment => !comment.commentId
    );
    if (pending.length === 0) {
      return Promise.resolve();
    }
    return Promise.all(
      pending.map(comment =>
        firstValueFrom(
          this.operationalReportService.createComment(id, {
            content: {
              text: comment.text,
              createdAt: comment.createdAt.toISOString(),
              authorName: comment.authorName,
            },
          })
        )
      )
    ).then(() => undefined);
  }

  onCancelOperation(): void {
    // Dient sowohl als "Abbrechen" (neu/bearbeiten) als auch als "Zurück"
    // (ansehen) – in beiden Fällen geht es zurück zur Liste. Nur im
    // Bearbeiten-Fall mit tatsächlich ungespeicherten Änderungen wird vorher
    // noch einmal nachgefragt (siehe hasUnsavedChanges()).
    if (!this.hasUnsavedChanges()) {
      this.router.navigate(['/operation-report']);
      return;
    }

    const data: ConfirmDialogData = {
      title: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CANCEL_CONFIRM_TITLE'
      ),
      message: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CANCEL_CONFIRM_MESSAGE'
      ),
      confirmText: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CANCEL_CONFIRM_DISCARD'
      ),
      cancelText: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CANCEL_CONFIRM_CONTINUE'
      ),
      danger: true,
    };
    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.router.navigate(['/operation-report']);
        }
      });
  }

  isSaving = signal(false);

  onSaveOperation(): void {
    this.persistReport(this.operationStatus());
  }

  onCompleteOperation(): void {
    // Unwiderruflich (siehe persistReport()/UI: die Wetter-Konfiguration
    // wird ab dem 1. Speichern "eingefroren", ein erneutes Öffnen/
    // Bearbeiten nach dem Abschließen ist nicht vorgesehen) – deshalb immer
    // eine Bestätigung, unabhängig von hasUnsavedChanges().
    const data: ConfirmDialogData = {
      title: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CLOSE_CONFIRM_TITLE'
      ),
      message: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CLOSE_CONFIRM_MESSAGE'
      ),
      confirmText: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CLOSE_CONFIRM_CONFIRM'
      ),
      cancelText: this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.CLOSE_CONFIRM_CANCEL'
      ),
      danger: true,
    };
    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.operationStatus.set('closed');
          this.operationClosedByName.set(this.currentUserDisplayName());
          this.persistReport('closed');
        }
      });
  }

  // Nur die "inhaltlichen" Felder, die tatsächlich verloren gehen könnten:
  // Weder Wetter-Konfiguration (nur vor dem 1. Speichern änderbar, per
  // Zahnrad-Menü jederzeit neu wählbar, kein "Verlust" im eigentlichen
  // Sinne) noch bereits einzeln gespeicherte Kommentare (bei bestehenden
  // Reports sofort persistiert) werden hier verfolgt.
  private originalContentSnapshot: {
    leaderName: string;
    categories: string;
    situationReport: string;
  } | null = null;

  private captureOriginalSnapshot(): void {
    this.originalContentSnapshot = {
      leaderName: this.leaderNameControl.value ?? '',
      categories: JSON.stringify(this.operationCategories()),
      situationReport: this.situationReportControl.value ?? '',
    };
  }

  private hasUnsavedChanges(): boolean {
    if (this.isReadonly()) {
      return false;
    }
    // Bei "Neu" ist jeder Kommentar zwangsläufig noch unpersistiert (siehe
    // addComment()) – ginge beim Abbrechen also verloren.
    if (!this.opReportId() && this.operationComments().length > 0) {
      return true;
    }
    if (!this.originalContentSnapshot) {
      return false;
    }
    return (
      (this.leaderNameControl.value ?? '') !==
        this.originalContentSnapshot.leaderName ||
      JSON.stringify(this.operationCategories()) !==
        this.originalContentSnapshot.categories ||
      (this.situationReportControl.value ?? '') !==
        this.originalContentSnapshot.situationReport
    );
  }

  private buildContent(status: 'open' | 'closed'): OpReportContent {
    return {
      status,
      categories: this.operationCategories(),
      situationReport: this.situationReportControl.value ?? '',
      leaderName: this.leaderNameControl.value ?? '',
      openedByName: this.operationOpenedByName(),
      closedByName: this.operationClosedByName() ?? undefined,
    };
  }

  // "Speichern" und "Einsatz abschließen" führen beide zurück zur Liste –
  // ein Verbleiben auf der Seite mit weiteren Save-Varianten (z.B.
  // "Speichern und bleiben") würde den User nur mit zu vielen Optionen
  // überfordern.
  private persistReport(status: 'open' | 'closed'): void {
    const content = this.buildContent(status);
    const id = this.opReportId();
    this.isSaving.set(true);

    if (id) {
      // Update: nur endDtg + content – die Wetter-Snapshots werden nur bei
      // der Neuanlage gesetzt und danach nicht mehr verändert. Kommentare
      // sind an dieser Stelle immer schon einzeln gespeichert (siehe
      // addComment()), müssen hier also nicht mehr nachgereicht werden.
      const body: UpdateOpReportBody = {
        endDtg: status === 'closed' ? moment().toISOString() : null,
        content,
      };
      this.operationalReportService
        .updateReport(this.customerId, id, body)
        .subscribe(() => {
          this.isSaving.set(false);
          this.router.navigate(['/operation-report']);
        });
      return;
    }

    // Neuanlage: die aktuell geladenen Wetterdaten (egal ob schon als
    // *ResponseChange-Output angekommen oder noch die letzte über
    // select*() gesetzte Konfiguration) werden als Snapshot mitgespeichert.
    const body: CreateOpReportBody = {
      startDtg: this.operationOpenedAt().toISOString(),
      endDtg: status === 'closed' ? moment().toISOString() : null,
      content,
      egp: this.egpConfig()
        ? { config: this.egpConfig(), data: this.egpLiveResponse() }
        : null,
      egw: this.egwConfig()
        ? { config: this.egwConfig(), data: this.egwLiveDisplayed }
        : null,
      epp: this.eppConfig()
        ? { config: this.eppConfig(), data: this.eppLiveResponse }
        : null,
      smw: this.smwConfig()
        ? { config: this.smwConfig(), data: this.smwLiveResponse }
        : null,
    };

    this.operationalReportService
      .createReport(this.customerId, body)
      .subscribe(created => {
        this.opReportId.set(created.id);
        // Bis hierhin nur lokal vorgemerkte Kommentare (siehe addComment())
        // jetzt mit der frisch vergebenen id nachreichen, bevor es zurück
        // zur Liste geht.
        this.flushPendingComments(created.id).then(() => {
          this.isSaving.set(false);
          this.router.navigate(['/operation-report']);
        });
      });
  }

  // --- PDF-Export ----------------------------------------------------------

  async exportAsPdf(): Promise<void> {
    if (this.isExportingPdf()) {
      return;
    }
    // Löst per @if (isExportingPdf()) im Template ein vollflächiges
    // app-loading-overlay aus (siehe operational-report.edit.component.html)
    // – deckt die Seite ab, während sich währenddessen sichtbar einiges
    // verschiebt (Theme-Umschaltung, feste Export-Breite, ausgeblendete
    // Buttons/Eingabefelder), und macht dem User klar, dass das erwartet
    // ist und die Seite gleich wieder normal aussieht. Ersetzt die vorher
    // genutzte Snackbar, die dasselbe Problem (Button-Spinner rendert wegen
    // der html2canvas-Main-Thread-Blockade nicht zuverlässig) nur mit einem
    // deutlich unauffälligeren Hinweis gelöst hatte.
    this.isExportingPdf.set(true);

    // html2canvas rendert die Seite exakt so, wie sie gerade dargestellt
    // wird – im Dark Mode also mit dunklen Kartenhintergründen etc., was in
    // einem gedruckten PDF keinen Sinn ergibt. Für die Erfassung deshalb
    // hart auf Light Mode umschalten und den ursprünglichen Modus danach
    // wiederherstellen (nur falls tatsächlich umgeschaltet wurde).
    const previousTheme = this.themeService.theme();
    const themeSwitchedForExport = previousTheme === 'dark';
    if (themeSwitchedForExport) {
      this.themeService.setTheme('light');
    }

    const hostElement = this.elementRef.nativeElement;
    // Klappt die für die Dashboard-Kachel-Ansicht auf feste Höhe begrenzten
    // Scroll-Container (EPP-Tabelle/EGP-Text/Warnungen) vollständig aus,
    // siehe .pdf-export-mode in operational-report.edit.component.scss.
    hostElement.classList.add('pdf-export-mode');

    const formEl = hostElement.querySelector<HTMLElement>('.operation-form');
    const weatherNowEl = hostElement.querySelector<HTMLElement>('.weather-now');
    const eppEl = hostElement.querySelector<HTMLElement>('.weather-now__epp');

    try {
      // Zwei verschachtelte requestAnimationFrame-Aufrufe statt nur einem:
      // ein einzelner rAF-Callback läuft VOR dem nächsten Paint, die darin
      // aufgelöste Promise wird aber als Microtask direkt danach (immer noch
      // vor dem Paint) weiterverarbeitet – der Browser käme so nie dazu,
      // den per isExportingPdf()/.pdf-export-mode ausgelösten Spinner
      // tatsächlich zu zeichnen, bevor die rechenintensive html2canvas-
      // Erfassung den Main-Thread blockiert. Der zweite rAF verzögert die
      // Fortsetzung auf den nächsten Frame, also NACH dem Paint des ersten.
      await new Promise<void>(resolve =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );

      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);

      const pdf = new jsPDF('p', 'mm', 'a4');
      // Verdana selbst ist keine der 14 in jsPDF eingebauten Standard-PDF-
      // Fonts (Helvetica/Times/Courier je 4 Stile + Symbol/ZapfDingbats)
      // und dürfte als proprietäre MS-Schriftart ohnehin nicht ohne
      // Weiteres als TTF mit eingebettet werden. DejaVu Sans (Bitstream
      // Vera Lizenz, siehe src/assets/fonts/DEJAVU-LICENSE.txt) ist eine
      // frei einbettbare, optisch nahe Alternative – beide sind
      // großzügige, für Bildschirmlesbarkeit optimierte humanistische
      // Grotesk-Schriften mit ähnlich großer x-Höhe. Kein Pixel-Match, aber
      // deutlich näher als Helvetica. Schlägt das Laden fehl (z.B. Netzwerk-
      // /Asset-Problem), fällt der Export auf Helvetica zurück, statt ganz
      // abzubrechen.
      const [dejaVuRegular, dejaVuBold] = await Promise.all([
        this.loadFontForPdf('/assets/fonts/DejaVuSans.ttf'),
        this.loadFontForPdf('/assets/fonts/DejaVuSans-Bold.ttf'),
      ]);
      if (dejaVuRegular) {
        pdf.addFileToVFS('DejaVuSans.ttf', dejaVuRegular);
        pdf.addFont('DejaVuSans.ttf', 'DejaVuSans', 'normal');
        if (dejaVuBold) {
          pdf.addFileToVFS('DejaVuSans-Bold.ttf', dejaVuBold);
          pdf.addFont('DejaVuSans-Bold.ttf', 'DejaVuSans', 'bold');
        }
        pdf.setFont('DejaVuSans', 'normal');
      } else {
        pdf.setFont('helvetica', 'normal');
      }
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const marginMm = 10;
      const contentWidthMm = pageWidthMm - marginMm * 2;

      // Fließ-Layout statt einer eigenen Seite pro Abschnitt: alle drei
      // Erfassungen (Formular/Wetter-jetzt/EPP-Tabelle) teilen sich eine
      // gemeinsame Y-Position, die über placeCanvasFlowing() fortlaufend
      // weitergereicht wird – so nutzt z.B. der Anfang des Wetter-jetzt-
      // Bereichs den nach dem Formular übrig gebliebenen Platz auf Seite 1
      // mit, statt dass eine neue Seite mit viel Weißraum beginnt.
      const flowState = { currentY: marginMm };

      // Logo-Header oben auf Seite 1 – dasselbe Bild wie im nativen
      // Druck-Header (siehe print-header--logo in navbar.component.html),
      // linksbündig, rechts die Kontaktinfos aus dem nativen Druck-Footer
      // (siehe print-footer in navbar.component.html), unten eine zarte
      // graue Trennlinie (#cccccc, dieselbe print-sichere Randfarbe wie
      // --glass-border in @media print unten) statt einer dicken blauen.
      const logo = await this.loadImageForPdf(
        '/assets/brand/meteofocus-logo-350.png'
      );
      if (logo) {
        const logoHeightMm = 9;
        const logoWidthMm = (logo.width * logoHeightMm) / logo.height;

        pdf.setFontSize(8);
        pdf.setTextColor('#4b5563');
        const rightX = pageWidthMm - marginMm;
        const lineHeightMm = 3.2;
        let textY = flowState.currentY + 3;
        pdf.text(
          'Wettermanufaktur GmbH, Bessemerstr.16, 12103 Berlin',
          rightX,
          textY,
          {
            align: 'right',
          }
        );
        textY += lineHeightMm;
        pdf.text(
          'www.wettermanufaktur.de, info@wettermanufaktur.de',
          rightX,
          textY,
          {
            align: 'right',
          }
        );
        pdf.setTextColor('#000000');

        const headerHeightMm = Math.max(logoHeightMm, lineHeightMm * 2 + 3);
        pdf.addImage(
          logo.dataUrl,
          'PNG',
          marginMm,
          flowState.currentY,
          logoWidthMm,
          logoHeightMm
        );

        flowState.currentY += headerHeightMm + 3;
        pdf.setDrawColor('#cccccc');
        pdf.setLineWidth(0.2);
        pdf.line(
          marginMm,
          flowState.currentY,
          pageWidthMm - marginMm,
          flowState.currentY
        );
        flowState.currentY += 6;
      }

      // Titel als echter PDF-Text (kein Rasterbild nötig) – war bisher nie
      // im Export enthalten, da .print-title außerhalb der erfassten
      // .operation-form liegt.
      const titleText = this.translate.instant(
        'PRODUCT.OPREPORT.EDIT.PRINT_TITLE'
      );
      pdf.setFontSize(15);
      pdf.text(titleText, marginMm, flowState.currentY + 5);
      flowState.currentY += 10;

      // scale:1.3 statt 1.5 spart nochmal ca. 25% Dateigröße (Pixelzahl
      // sinkt quadratisch mit dem Scale-Faktor: (1.3/1.5)² ≈ 0,75). JPEG
      // statt PNG wurde bereits früher testweise verglichen, komprimiert
      // für diesen Inhalt (viel Weißraum, scharfe Tabellenlinien/Text)
      // aber kaum besser als PNG und riskiert Kompressionsartefakte am
      // Text – deshalb bei PNG (verlustfrei) geblieben.
      const captureCanvas = (element: HTMLElement) =>
        html2canvas(element, {
          scale: 1.3,
          backgroundColor: '#ffffff',
          useCORS: true,
        });

      // Einsatz-Formular
      if (formEl) {
        const canvas = await captureCanvas(formEl);
        this.placeCanvasFlowing(
          pdf,
          canvas,
          marginMm,
          contentWidthMm,
          pageHeightMm,
          flowState
        );
      }

      // Wetter jetzt (EGP/Warnungen/Messwerte) – EPP-Tabelle für diese
      // Erfassung ausblenden, sie wird separat (und ggf. über mehrere
      // Seiten verteilt) angehängt.
      if (weatherNowEl) {
        const originalEppDisplay = eppEl?.style.display ?? '';
        if (eppEl) {
          eppEl.style.display = 'none';
        }
        const canvas = await captureCanvas(weatherNowEl);
        if (eppEl) {
          eppEl.style.display = originalEppDisplay;
        }

        // Erst JETZT (mit bekannter Canvas-Höhe) entscheiden, ob der
        // komplette Wetter-Bereich noch auf die aktuelle Seite passt.
        // placeCanvasFlowing() würde sonst einfach so viel wie möglich auf
        // die aktuelle Seite quetschen und den Rest umbrechen – bei einem
        // längeren Formular/mehr Kommentaren mitten durch eine der
        // Wetter-Kacheln (EGP/Warnungen/Messwerte). Anders als bei der
        // EPP-Tabelle darunter (die als Tabelle "sauber" umbrechen darf und
        // bewusst immer eine eigene Seite bekommt) soll dieser Bereich nur
        // vollständig oder gar nicht auf eine Seite passen.
        const mmPerPx = contentWidthMm / canvas.width;
        const weatherHeightMm = canvas.height * mmPerPx;
        // 2mm Abstand vor + 5mm nach der Trennlinie, siehe unten.
        const dividerAllowanceMm = 7;
        const remainingMm = pageHeightMm - marginMm - flowState.currentY;

        if (weatherHeightMm + dividerAllowanceMm <= remainingMm) {
          // Passt komplett auf die aktuelle Seite – dünne graue
          // Trennlinie zum vorherigen Abschnitt, damit "Wetter jetzt"
          // klar abgegrenzt ist statt direkt am Formular zu kleben
          // (dieselbe print-sichere Randfarbe wie beim Logo-Header oben,
          // #cccccc). Die Linie selbst markiert den neuen Abschnitt
          // bereits eindeutig, daher bewusst nur knapper Abstand.
          flowState.currentY += 2;
          pdf.setDrawColor('#cccccc');
          pdf.setLineWidth(0.2);
          pdf.line(
            marginMm,
            flowState.currentY,
            pageWidthMm - marginMm,
            flowState.currentY
          );
          flowState.currentY += 5;
        } else {
          // Passt nicht mehr komplett auf die aktuelle Seite – lieber
          // vollständig auf einer neuen Seite beginnen (keine Trennlinie
          // mehr nötig, der Seitenumbruch trennt bereits eindeutig).
          pdf.addPage();
          flowState.currentY = marginMm;
        }

        this.placeCanvasFlowing(
          pdf,
          canvas,
          marginMm,
          contentWidthMm,
          pageHeightMm,
          flowState
        );
      }

      // Einsatzpunktprognose-Tabelle: bekommt bewusst wieder eine eigene,
      // neue Seite (statt wie Formular/Wetter jetzt nahtlos anzuschließen)
      // – sie ist der mit Abstand längste, tabellarische Abschnitt und soll
      // dadurch klar von "Wetter jetzt" abgegrenzt bleiben. Verteilt sich
      // aber weiterhin über so viele Folgeseiten wie nötig (kein Browser-
      // Seitenumbruch mehr nötig, da hier ein fertiges Bild einfach in
      // Höhen-Scheiben zerlegt wird).
      if (eppEl) {
        pdf.addPage();
        flowState.currentY = marginMm;

        // Erzwingt height:auto/overflow:visible per Inline-Style (mit
        // !important) auf JEDEM Element zwischen .dashboard-table-container
        // und .weather-now__epp. Per Diagnose bestätigt: Zwischen-Elemente
        // wie .dashboard-epp-table-container/app-dashboard-epp blieben trotz
        // korrektem, mehrfach überprüftem CSS (keine widersprüchliche Regel
        // im gesamten Projekt gefunden) bei ihrer alten (325px) Höhe hängen
        // – ein Layout-Reflow-Effekt bei dieser tiefen Verschachtelung,
        // dessen genaue Ursache sich nicht auffinden ließ. Inline-Styles
        // gewinnen garantiert unabhängig von der (unbekannten) CSS-Quelle.
        const forcedElements: HTMLElement[] = [];
        let ancestor: HTMLElement | null = eppEl.querySelector<HTMLElement>(
          '.dashboard-table-container'
        );
        while (ancestor) {
          ancestor.style.setProperty('height', 'auto', 'important');
          ancestor.style.setProperty('max-height', 'none', 'important');
          ancestor.style.setProperty('overflow', 'visible', 'important');
          forcedElements.push(ancestor);
          if (ancestor === eppEl) {
            break;
          }
          ancestor = ancestor.parentElement;
        }
        await new Promise<void>(resolve =>
          requestAnimationFrame(() => resolve())
        );

        const canvas = await captureCanvas(eppEl);

        // Passt die Tabelle bei voller Breite nur KNAPP nicht mehr auf die
        // eine (gerade frisch begonnene) Seite, lieber geringfügig
        // verkleinert komplett draufpacken statt eine einzelne Zeile auf
        // eine zweite Seite überlaufen zu lassen. Bei deutlich längeren
        // Tabellen (viele EPP-Zeilen) bleibt es dagegen bei der normalen
        // Aufteilung über mehrere Seiten – sonst würde der Text zu klein.
        const MIN_FIT_TO_PAGE_SCALE = 0.75;
        const fullWidthMmPerPx = contentWidthMm / canvas.width;
        const naturalHeightMm = canvas.height * fullWidthMmPerPx;
        const availableHeightMm = pageHeightMm - marginMm * 2;
        const fitScale = availableHeightMm / naturalHeightMm;

        if (fitScale < 1 && fitScale >= MIN_FIT_TO_PAGE_SCALE) {
          const scaledWidthMm = contentWidthMm * fitScale;
          const xOffsetMm = marginMm + (contentWidthMm - scaledWidthMm) / 2;
          pdf.addImage(
            canvas.toDataURL('image/png'),
            'PNG',
            xOffsetMm,
            flowState.currentY,
            scaledWidthMm,
            availableHeightMm
          );
          flowState.currentY += availableHeightMm;
        } else {
          this.placeCanvasFlowing(
            pdf,
            canvas,
            marginMm,
            contentWidthMm,
            pageHeightMm,
            flowState
          );
        }

        for (const el of forcedElements) {
          el.style.removeProperty('height');
          el.style.removeProperty('max-height');
          el.style.removeProperty('overflow');
        }
      }

      const reportId = this.opReportId();
      pdf.save(
        `Einsatzprotokoll${reportId ? '_' + reportId : ''}_${moment().format('YYYY-MM-DD_HHmm')}.pdf`
      );
    } finally {
      hostElement.classList.remove('pdf-export-mode');
      if (themeSwitchedForExport) {
        this.themeService.setTheme(previousTheme);
      }
      this.isExportingPdf.set(false);
    }
  }

  // Lädt ein Bild aus /assets als Data-URL (+ Originalgröße für die
  // Seitenverhältnis-Berechnung) für pdf.addImage(). Gibt bei einem
  // Ladefehler null zurück, statt den ganzen Export abzubrechen – das
  // Logo ist ein "nice to have", kein kritischer Bestandteil des PDFs.
  private loadImageForPdf(
    src: string
  ): Promise<{ dataUrl: string; width: number; height: number } | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  // Lädt eine TTF-Schriftdatei aus /assets als reinen Base64-String (ohne
  // "data:...;base64,"-Präfix) für pdf.addFileToVFS()/addFont(). Der Umweg
  // über FileReader.readAsDataURL() statt btoa(String.fromCharCode(...))
  // ist bewusst gewählt – Letzteres sprengt bei einer ~700KB-Schriftdatei
  // das Argumentlimit von Function.prototype.apply/Spread-Syntax. Gibt bei
  // einem Ladefehler null zurück, der Aufrufer fällt dann auf einen der
  // eingebauten PDF-Standardfonts zurück, statt den ganzen Export
  // abzubrechen.
  private async loadFontForPdf(url: string): Promise<string | null> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const blob = await response.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      const base64Start = dataUrl.indexOf(',');
      return base64Start >= 0 ? dataUrl.slice(base64Start + 1) : null;
    } catch {
      return null;
    }
  }

  // Platziert ein (ggf. sehr hohes) Canvas fortlaufend ab state.currentY,
  // zerlegt es dafür bei Bedarf in Höhen-Scheiben und verteilt diese über
  // so viele Seiten wie nötig. Im Gegensatz zu einer festen "eine Seite
  // pro Abschnitt"-Aufteilung setzt jeder Aufruf direkt dort fort, wo der
  // vorherige aufgehört hat – der nach einem kurzen Abschnitt übrige Platz
  // auf der aktuellen Seite wird so vom nächsten Abschnitt mitgenutzt
  // statt als Weißraum zu verschwinden. state wird dabei mutiert, damit
  // aufeinanderfolgende Aufrufe nahtlos aneinander anschließen.
  private placeCanvasFlowing(
    pdf: import('jspdf').jsPDF,
    canvas: HTMLCanvasElement,
    marginMm: number,
    contentWidthMm: number,
    pageHeightMm: number,
    state: { currentY: number }
  ): void {
    // Bleibt auf der aktuellen Seite weniger als das übrig, wird direkt
    // eine neue Seite begonnen, statt eine kaum lesbare Mini-Scheibe dort
    // noch reinzuquetschen.
    const MIN_USEFUL_REMAINING_MM = 15;
    const mmPerPx = contentWidthMm / canvas.width;
    let renderedPx = 0;

    while (renderedPx < canvas.height) {
      let remainingMm = pageHeightMm - marginMm - state.currentY;
      if (remainingMm < MIN_USEFUL_REMAINING_MM) {
        pdf.addPage();
        state.currentY = marginMm;
        remainingMm = pageHeightMm - marginMm - state.currentY;
      }

      const remainingPx = Math.floor(remainingMm / mmPerPx);
      const sliceHeightPx = Math.min(remainingPx, canvas.height - renderedPx);

      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext('2d');
      ctx?.drawImage(
        canvas,
        0,
        renderedPx,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx
      );

      const dataUrl = sliceCanvas.toDataURL('image/png');
      const sliceHeightMm = sliceHeightPx * mmPerPx;
      pdf.addImage(
        dataUrl,
        'PNG',
        marginMm,
        state.currentY,
        contentWidthMm,
        sliceHeightMm
      );

      state.currentY += sliceHeightMm;
      renderedPx += sliceHeightPx;

      if (renderedPx < canvas.height) {
        pdf.addPage();
        state.currentY = marginMm;
      }
    }
  }
}
